<template>
  <div v-if="isRenderable" class="badge-duplex" :class="duplexClass">
    <div class="badge-wrap" :class="layoutClass">
      <div class="badge-card" :style="cardStyle">
        <img v-if="bgSrc" class="badge-bg" :src="bgSrc" alt="" @error="onBgError" />
        <div
          v-if="card.displayName"
          class="badge-text badge-text--name"
          :title="card.displayName"
          :style="textStyle(card.nameSlot)"
        >
          {{ card.displayName }}
        </div>
        <div
          v-if="card.identityTitle"
          class="badge-text badge-text--identity"
          :title="card.identityTitle"
          :style="textStyle(card.identitySlot)"
        >
          {{ card.identityTitle }}
        </div>
      </div>
      <p v-if="showBack" class="badge-side-label">正面</p>
    </div>
    <div v-if="showBack" class="badge-wrap badge-wrap--back" :class="layoutClass">
      <div class="badge-card">
        <img v-if="bgSrc" class="badge-bg" :src="bgSrc" alt="" />
        <img v-if="qrDataUrl" class="badge-qr" :src="qrDataUrl" alt="扫码查看胸卡" />
        <p v-else class="badge-qr-miss">{{ qrMissText }}</p>
        <p class="badge-qr-hint">扫码查看胸卡</p>
      </div>
      <p class="badge-side-label">反面（二维码）</p>
      <p v-if="qrScanHint" class="badge-qr-scan-hint">{{ qrScanHint }}</p>
    </div>
  </div>
  <p v-else class="badge-unavail">{{ (card && card.message) || '暂无法展示胸卡' }}</p>
</template>

<script>
import QRCode from 'qrcode'
import { BASE_URL, resolvePublicApiBase } from '@/api'
import { slotToPreviewCss } from '@/utils/badgeTextSlotStyle.js'

export default {
  name: 'ActivityBadgeCardPreview',
  props: {
    card: { type: Object, default: null },
    scale: { type: Number, default: 1 },
    /** 是否展示反面（同底图 + 扫码二维码） */
    showBack: { type: Boolean, default: false },
    /** 正反面布局：stack 上下大图；row 宽屏时左右并排 */
    duplexLayout: {
      type: String,
      default: 'stack',
      validator: (v) => v === 'stack' || v === 'row',
    },
  },
  data() {
    return { bgLoadFailed: false, qrDataUrl: '' }
  },
  computed: {
    isRenderable() {
      if (!this.card) return false
      if (this.card.available === false) return false
      const hasImg = !!String(this.card.imageUrl || '').trim()
      const hasText = !!(this.card.displayName || this.card.identityTitle)
      return hasImg || hasText
    },
    layoutClass() {
      const t = String((this.card && this.card.layoutType) || 'LANDSCAPE').toUpperCase()
      if (t === 'PORTRAIT') return 'badge-wrap--portrait'
      if (t === 'SQUARE') return 'badge-wrap--square'
      return 'badge-wrap--landscape'
    },
    bgSrc() {
      const u = String((this.card && this.card.imageUrl) || '').trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u
      return `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`
    },
    cardStyle() {
      const s = Number(this.scale)
      const scale = Number.isFinite(s) && s > 0 ? s : 1
      return scale === 1 ? {} : { transform: `scale(${scale})`, transformOrigin: 'top center' }
    },
    qrMissText() {
      const u = String((this.card && this.card.shareQrUrl) || '').trim()
      if (!u) return '预览模式无扫码链接'
      return '二维码生成中…'
    },
    duplexClass() {
      if (!this.showBack) return 'badge-duplex--single'
      return this.duplexLayout === 'row' ? 'badge-duplex--row' : 'badge-duplex--stack'
    },
    layoutTypeUpper() {
      return String((this.card && this.card.layoutType) || 'LANDSCAPE').toUpperCase()
    },
    qrScanHint() {
      if (!this.showBack) return ''
      const raw = String((this.card && this.card.shareQrUrl) || '').trim()
      if (!raw) {
        return '后端未返回扫码链接：请在 application.properties 配置 app.public-base-url=https://你的公网域名（与 cpolar 一致），重启后端后重新打开本预览。'
      }
      const abs = this.absolutizeShareUrl(raw)
      if (/^https:\/\//i.test(abs) && !this.isLocalOrPrivateUrl(abs)) {
        return ''
      }
      if (/^https:\/\//i.test(raw) && !this.isLocalOrPrivateUrl(raw)) {
        return ''
      }
      if (raw.startsWith('/') || !/^https?:\/\//i.test(raw)) {
        const hasAdminBase = !!String(BASE_URL || '').trim()
        if (!hasAdminBase) {
          return '后端未配置 app.public-base-url，且后台未设置 VUE_APP_API_BASE。二维码会指向本机地址，手机微信扫不开。请在后端 application.properties 填写 app.public-base-url=https://你的cpolar域名（勿尾斜杠），重启后端并重新打开预览；也可在 huiyuanguanli_houtai/.env.development 增加 VUE_APP_API_BASE=同一域名。'
        }
        return '后端 application.properties 中 app.public-base-url 未配置为 https 公网地址，请填写与手机可访问的 API 域名一致后重启后端，再重新打开胸卡预览。'
      }
      return `当前二维码为 ${abs.split('?')[0]} …，非手机可访问的 https 公网链接。请配置 app.public-base-url 后重启后端。`
    },
  },
  watch: {
    card: {
      deep: true,
      handler() {
        this.bgLoadFailed = false
        this.refreshQr()
      },
    },
    showBack(v) {
      if (v) this.refreshQr()
    },
  },
  mounted() {
    if (this.showBack) this.refreshQr()
  },
  methods: {
    textStyle(slot) {
      return slotToPreviewCss(slot, this.layoutTypeUpper)
    },
    isLocalOrPrivateUrl(url) {
      try {
        const h = new URL(url).hostname.toLowerCase()
        if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0') return true
        if (/^192\.168\./.test(h) || /^10\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h)) return true
      } catch {
        return false
      }
      return false
    },
    absolutizeShareUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u
      const base = resolvePublicApiBase()
      if (base) {
        return base + (u.startsWith('/') ? '' : '/') + u
      }
      return u
    },
    onBgError() {
      this.bgLoadFailed = true
    },
    async refreshQr() {
      if (!this.showBack) {
        this.qrDataUrl = ''
        return
      }
      const raw = String((this.card && this.card.shareQrUrl) || '').trim()
      const url = this.absolutizeShareUrl(raw)
      if (!url) {
        this.qrDataUrl = ''
        return
      }
      try {
        this.qrDataUrl = await QRCode.toDataURL(url, { margin: 2, width: 280 })
      } catch {
        this.qrDataUrl = ''
      }
    },
  },
}
</script>

<style scoped>
.badge-duplex {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  width: 100%;
}
.badge-duplex--row {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 24px;
}
.badge-duplex--single {
  gap: 0;
}
.badge-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(520px, 100%);
  flex: 0 0 auto;
}
.badge-duplex--row .badge-wrap {
  width: min(460px, calc(50% - 16px));
}
.badge-side-label {
  margin: 10px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}
.badge-card {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.14);
  background: #f8fafc;
  border: 1px solid #e4e7ec;
}
.badge-wrap--landscape .badge-card {
  aspect-ratio: 1.586 / 1;
}
.badge-wrap--portrait .badge-card {
  aspect-ratio: 1 / 1.586;
  width: min(400px, 100%);
}
.badge-wrap--square .badge-card {
  aspect-ratio: 1 / 1;
  width: min(440px, 100%);
}
.badge-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.badge-text {
  position: absolute;
  z-index: 2;
  max-width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.35);
}
.badge-qr {
  position: absolute;
  z-index: 3;
  left: 50%;
  top: 46%;
  transform: translate(-50%, -50%);
  width: 38%;
  max-width: 200px;
  aspect-ratio: 1 / 1;
  background: #fff;
  padding: 8px;
  border-radius: 8px;
  box-sizing: border-box;
}
.badge-qr-miss {
  position: absolute;
  z-index: 3;
  left: 50%;
  top: 46%;
  transform: translate(-50%, -50%);
  font-size: 13px;
  color: #475467;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 14px;
  border-radius: 8px;
  margin: 0;
}
.badge-qr-hint {
  position: absolute;
  z-index: 3;
  left: 50%;
  bottom: 14%;
  transform: translateX(-50%);
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #f8fafc;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
}
.badge-unavail {
  color: #667085;
  font-size: 14px;
  margin: 12px 0;
}
.badge-qr-scan-hint {
  margin: 8px 0 0;
  max-width: 520px;
  font-size: 12px;
  line-height: 1.5;
  color: #b42318;
  text-align: center;
}
</style>
